import { BigNumber, ethers } from 'ethers';
import { useEffect, useState, useLayoutEffect } from 'react';
import './App.scss';

let ypos = [];
let interval;

function useWindowSize() {
  const [size, setSize] = useState([0, 0]);
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

const UNITS = [
  {name: "Wei", exponent: 0},
  {name: "Kwei", exponent: 3},
  {name: "Mwei", exponent: 6},
  {name: "Gwei", exponent: 9},
  {name: "Microether", exponent: 12},
  {name: "Milliether", exponent: 15},
  {name: "Ether", exponent: 18},
  {name: "Kether", exponent: 21},
  {name: "Mether", exponent: 24},
  {name: "Gether", exponent: 27},
  {name: "Tether", exponent: 30},
];

function App() {
  const [wei, setWei] = useState("1000000000000000000");
  const [lastEntry, setLastEntry] = useState(null); // {exponent, text}
  const [hasError, setHasError] = useState(false);

  const [width, height] = useWindowSize();

  const handleValueChange = (value, unit) => {
    setLastEntry({exponent: unit.exponent, text: value})
    if(!value) {
      setWei(null);
      return;
    }

    try {
      let strippedValue = value.replaceAll(",", "");
      const weiBn = ethers.utils.parseUnits(strippedValue, unit.exponent);
      setWei(weiBn.toString());
      setHasError(false);
    } catch (e) {
      setHasError(true);
    }
  }

  useEffect(() => {
    clearInterval(interval);

    const canvas = document.getElementById('canvas');
    const ctx = canvas.getContext('2d');

    let w = canvas.width = document.body.getBoundingClientRect().width;
    let h = canvas.height = document.body.getBoundingClientRect().height;
    const cols = Math.floor(w / 20) + 1;
    if(ypos.length !== cols) {
      ypos = Array(cols).fill(0);
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);
    }


    function matrix () {
      ctx.fillStyle = '#0002';
      ctx.fillRect(0, 0, w, h);
      
      ctx.fillStyle = '#0f0';
      ctx.font = '15pt monospace';
      
      ypos.forEach((y, ind) => {
        const text = String.fromCharCode(Math.random() * 128);
        const x = ind * 20;
        ctx.fillText(text, x, y);
        if (y > 100 + Math.random() * 10000) ypos[ind] = 0;
        else ypos[ind] = y + 20;
      });
    }

    interval = setInterval(matrix, 50);
  }, [width, height]);

  return (
    <div className="App">
      <div className="canvas-container">
        <canvas id="canvas"></canvas>
      </div>

      <div className="main-container">
        <header>
            <h1>Ethereum Unit Converter</h1>
            <div className="subtitle">By <a href="https://sylphdapps.com/" target="_blank" rel="noreferrer">Sylph Dapps</a></div>
          </header>
          <p>
            When using Ethereum you think of your address's balance in units called ether, but when pricing a transaction your wallet will ask you to enter an amount of ETH in units called gwei.
          </p>
          <p>
            One ether is one billion gwei. There are other denominations beyond these two, the smallest being one wei, which is one billionth of a gwei or one quintillionth of an ether.
          </p>
          <p>
            There are some lesser-known denominations as well. Use this tool to convert between them.
          </p>
          <div className="units">
            { UNITS.map(unit => {
              let str = "";
              if(lastEntry && lastEntry.exponent === unit.exponent) {
                str = lastEntry.text;
              } else {
                if(!hasError && wei !== null) {
                  const weiBn = BigNumber.from(wei);
                  const value = ethers.utils.formatUnits(weiBn, unit.exponent);
                  str = str = ethers.utils.commify(value);

                  // formatUnits outputs 5.0 instead of 5, so strip off the ".0" in that case
                  if(str.split(".").length === 2) {
                    if(str.split(".")[1] === "0") {
                      str = str.split(".")[0];
                    }
                  }
                }
              }

              return (
                <div key={unit.exponent} className="unit">
                  <div className="input">
                    <input
                      value={str}
                      onChange={e => handleValueChange(e.target.value, unit)}
                    />
                  </div>
                  <div className="label">{unit.name}</div>
                  <div className="wei">
                    { unit.exponent !== 18 &&
                      <div>10<sup>{unit.exponent - 18}</sup> ether</div>
                    }
                  </div>
                </div>
              )
            })}
          </div>
          <p>
            Wei takes its name from cryptographer <a href="https://en.wikipedia.org/wiki/Wei_Dai" target="_blank" rel="noreferrer">Wei Dai</a>. Several of the other denominations have alternative names that pay homage to renowned computer scientists.
          </p>
          <ul>
            <li>The Kwei, or babbage, is named after <a href="https://en.wikipedia.org/wiki/Charles_Babbage" target="_blank" rel="noreferrer">Charles Babbage</a>.</li>
            <li>The Mwei, or ada, is named after <a href="https://en.wikipedia.org/wiki/Ada_Lovelace" target="_blank" rel="noreferrer">Ada Lovelace</a>.</li>
            <li>The Gwei, or shannon, is named after <a href="https://en.wikipedia.org/wiki/Claude_Shannon" target="_blank" rel="noreferrer">Claude Shannon</a>.</li>
            <li>The microether, or szabo, is named after <a href="https://en.wikipedia.org/wiki/Nick_Szabo" target="_blank" rel="noreferrer">Nick Szabo</a>.</li>
            <li>The milliether, or finney, is named after <a href="https://en.wikipedia.org/wiki/Hal_Finney_(computer_scientist)" target="_blank" rel="noreferrer">Hal Finney</a>.</li>
          </ul>
          <p>
            Most of the time we communicate only in terms of ether, gwei, and wei. In fact, <a href="https://docs.soliditylang.org/en/v0.8.14/units-and-global-variables.html" target="_blank" rel="noreferrer">recent versions of Solidity</a> removed support for all but these three denominations.
          </p>
      </div>
    </div>
  );
}

export default App;
